import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IMuiSelectOption } from "../components/framework/muiAutocomplete2";
import { getVehicles } from "../services/vehicleService";
import { VehicleListItemDto } from "../models/transport/vehicle";
import { getVehicleGroups } from "../services/vehicleGroupService";
import { VehicleGroupItem } from "../models/vehicleGroup/vehicleGroupItem";
import { VehicleMaintenanceDto } from "../models/vehicle/vehicleMaintenance";
import * as vehicleMaintenanceService from "../services/vehicleMaintenanceService";

export interface VehicleState {
    vehicles: VehicleListItemDto[];
    options: IMuiSelectOption[];
    allVehicleGroupsLoading: boolean;
    allVehicleGroups: VehicleGroupItem[];
    vehicleMaintenances?: VehicleMaintenanceDto[];
    vehicleMaintenancesLoading: boolean;
}

export const initialState: VehicleState = {
    vehicles: null,
    options: null,
    allVehicleGroupsLoading: true,
    allVehicleGroups: [],
    vehicleMaintenances: [],
    vehicleMaintenancesLoading: false,
};

export const vehicleSlice = createSlice({
    name: "vehicle",
    initialState,
    reducers: {
        setVehicles: (state, action: PayloadAction<VehicleListItemDto[]>) => {
            state.vehicles = action.payload;
        },
        setOptions: (state, action: PayloadAction<IMuiSelectOption[]>) => {
            state.options = action.payload;
        },
        setAllVehicleGroups(state, action: PayloadAction<VehicleGroupItem[]>) {
            state.allVehicleGroups = action.payload;
        },
        setVehicleMaintenances(state, action: PayloadAction<VehicleMaintenanceDto[]>) {
            state.vehicleMaintenances = action.payload;
        },
        setVehicleMaintenancesLoading(state, action: PayloadAction<boolean>) {
            state.vehicleMaintenancesLoading = action.payload;
        },
        removeVehicleMaintenance(state, action: PayloadAction<string>) {
            state.vehicleMaintenances = state.vehicleMaintenances?.filter(vm => vm.id !== action.payload);
            return state;
        },
        addVehicleMaintenance(state, action: PayloadAction<VehicleMaintenanceDto>) {
            state.vehicleMaintenances.push(action.payload);
        },
        updateVehicleMaintenance(state, action: PayloadAction<VehicleMaintenanceDto>) {
            state.vehicleMaintenances = state.vehicleMaintenances.map((item: VehicleMaintenanceDto) => {
                if (item.id !== action.payload.id) return item;

                console.log(JSON.stringify(action.payload));
                return {
                    ...item,
                    ...action.payload
                };
            });
            return state;
        }
    }, extraReducers(builder) {
        builder
            .addCase(fetchAllVehicleGroups.pending, (state) => {
                state.allVehicleGroupsLoading = false;
            })
            .addCase(fetchAllVehicleGroups.fulfilled, (state, action) => {
                state.allVehicleGroupsLoading = true;
                state.allVehicleGroups = action.payload;
            })
            .addCase(fetchAllVehicleGroups.rejected, (state) => {
                state.allVehicleGroupsLoading = false;
            })
            .addCase(saveVehicleMaintenance.fulfilled, (state, action) => {
                const index = state.vehicleMaintenances.findIndex(i => i.id === action.payload.id);

                // Update vehicle maintenances by replacing existing or adding new
                if (index >= 0) {
                    state.vehicleMaintenances[index] = action.payload;
                } else {
                    state.vehicleMaintenances.push(action.payload);
                }
            });
    },
});

export const {
    setVehicles,
    setOptions,
    setAllVehicleGroups,
    setVehicleMaintenances,
    removeVehicleMaintenance,
    addVehicleMaintenance,
    updateVehicleMaintenance,
    setVehicleMaintenancesLoading
} = vehicleSlice.actions;

export const fetchVehicleOptions = createAsyncThunk(
    "vehicle/options",
    async (_, thunkApi) => {
        const vehicles = await getVehicles(false, thunkApi.signal);
        thunkApi.dispatch(setVehicles(vehicles.data));
        thunkApi.dispatch(
            setOptions(
                vehicles.data.map((v) => ({
                    value: v.id,
                    label: `${v.registerNumber}, ${v.brand}`,
                }))
            )
        );
    }
);

export const fetchAllVehicleGroups = createAsyncThunk(
    "monitorView/fetchAllVehicleGroups",
    async (request: string[], { rejectWithValue }) => {
        try {
            const response = await getVehicleGroups(request);
            return response.data;
        } catch (err) {
            return rejectWithValue("Failed to fetch vehicle groups");
        }
    }
);

/* Vehicle maintenance actions */
export const fetchVehicleMaintenanceData = createAsyncThunk(
    "vehicle/fetchVehicleMaintenanceData",
    async (
        {
            fromDate,
            untilDate,
            vehicleIds
        }: {
            fromDate: Date;
            untilDate?: Date;
            vehicleIds?: string[];
        },
        _thunkApi) => {
        _thunkApi.dispatch(setVehicleMaintenancesLoading(true));
        const maintenanceData = await vehicleMaintenanceService.getVehicleMaintenances(fromDate, untilDate, vehicleIds, _thunkApi.signal);
        _thunkApi.dispatch(setVehicleMaintenances(maintenanceData));
        _thunkApi.dispatch(setVehicleMaintenancesLoading(false));
    }
);


export const saveVehicleMaintenance = createAsyncThunk(
    "vehicle/saveVehicleMaintenance",
    async (
        {
            vehicleMaintenanceDto }:
            {
                vehicleMaintenanceDto: VehicleMaintenanceDto;
            },
        _thunkApi) => {
        const res = await vehicleMaintenanceService.saveVehicleMaintenance(vehicleMaintenanceDto, _thunkApi.signal);
        return res.data;
    }
);

export const deleteVehicleMaintenanceData = createAsyncThunk(
    "vehicle/deleteVehicleMaintenanceData",
    async (
        {
            vehicleMaintenanceId
        }: {
            vehicleMaintenanceId: string;
        },
        _thunkApi) => {
        await vehicleMaintenanceService.deleteVehicleMaintenance(vehicleMaintenanceId, _thunkApi.signal);

        _thunkApi.dispatch(removeVehicleMaintenance(vehicleMaintenanceId));
    }
);

export const vehicleReducer = vehicleSlice.reducer;
